/**
 * RelatedDataModal Component
 * 
 * Modal to display related data for Pointer and Relation fields.
 * - Pointer: Shows a single object's details in a key-value format
 * - Relation: Shows a list of related objects in a table format
 * 
 * Features:
 * - Fullscreen support via Portal
 * - Loading states
 * - Link to open in Dashboard
 * - Nested pointer support (clicking on pointers within the modal)
 */

import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useRelatedData, RelatedDataType } from '../../../../hooks/useRelatedData';
import { ParseRow } from '../../../../types/AppPanel';

interface RelatedDataModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Close modal callback */
  onClose: () => void;
  /** Type of related data */
  type: RelatedDataType;
  /** Target class name */
  targetClass: string;
  /** Object ID (for pointer) */
  objectId?: string;
  /** Source class (for relation) */
  sourceClass?: string;
  /** Source object ID (for relation) */
  sourceObjectId?: string;
  /** Relation field name (for relation) */
  relationField?: string;
  /** Parse Server URL */
  parseServerURL?: string;
  /** Application ID */
  parseAppId?: string;
  /** Master Key */
  masterKey?: string;
  /** Dashboard URL for "View in Dashboard" link */
  dashboardUrl?: string;
  /** Callback for clicking nested pointers */
  onPointerClick?: (pointer: { className: string; objectId: string }) => void;
}

/**
 * Patterns to identify sensitive fields that should be masked.
 */
const SENSITIVE_PATTERNS = [
  'sessiontoken', 'password', 'authdata', 'accesstoken', 
  'refreshtoken', 'apikey', 'secretkey', 'privatekey', 
  'token', '_hashed_password', 'bcryptpassword', 'secret', 'credential'
];

/**
 * Check if a field name indicates sensitive data
 */
function isSensitiveField(fieldName: string): boolean {
  const lower = fieldName.toLowerCase();
  return SENSITIVE_PATTERNS.some(pattern => lower.includes(pattern));
}

/**
 * Format a cell value for display
 */
function formatValue(value: unknown, fieldName?: string): string {
  if (value === null) return '(null)';
  if (value === undefined) return '(undefined)';
  
  // Mask sensitive fields
  if (fieldName && isSensitiveField(fieldName) && typeof value === 'string') {
    return '••••••••••••••••';
  }
  
  if (typeof value === 'object') {
    // Handle Parse types
    const obj = value as Record<string, unknown>;
    
    if (obj.__type === 'Date') {
      return new Date(obj.iso as string).toLocaleString();
    }
    if (obj.__type === 'Pointer') {
      return `→ ${obj.className}:${(obj.objectId as string).slice(0, 8)}...`;
    }
    if (obj.__type === 'File') {
      return `📎 ${obj.name}`;
    }
    if (obj.__type === 'GeoPoint') {
      return `📍 ${obj.latitude}, ${obj.longitude}`;
    }
    if (obj.__type === 'Relation') {
      return `🔗 Relation → ${obj.className}`;
    }
    if (Array.isArray(value)) {
      return `[${value.length} items]`;
    }
    
    // Generic object
    return JSON.stringify(value).slice(0, 50) + '...';
  }
  
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  
  if (typeof value === 'string' && value.length > 100) {
    return value.slice(0, 100) + '...';
  }
  
  return String(value);
}

/**
 * Check if a value is a pointer
 */
function isPointer(value: unknown): value is { __type: 'Pointer'; className: string; objectId: string } {
  return typeof value === 'object' && value !== null && (value as Record<string, unknown>).__type === 'Pointer';
}

/**
 * Get important fields to display first
 */
function sortFields(fields: string[]): string[] {
  const priority = ['objectId', 'name', 'title', 'username', 'email', 'createdAt', 'updatedAt'];
  
  return [...fields].sort((a, b) => {
    const aIdx = priority.indexOf(a);
    const bIdx = priority.indexOf(b);
    
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return a.localeCompare(b);
  });
}

export default function RelatedDataModal({
  isOpen,
  onClose,
  type,
  targetClass,
  objectId,
  sourceClass,
  sourceObjectId,
  relationField,
  parseServerURL,
  parseAppId,
  masterKey,
  dashboardUrl,
  onPointerClick,
}: RelatedDataModalProps) {
  // Fetch related data
  const { data, count, isLoading, error } = useRelatedData({
    parseServerURL,
    appId: parseAppId,
    masterKey,
    type,
    targetClass,
    objectId,
    sourceClass,
    sourceObjectId,
    relationField,
    enabled: isOpen,
  });

  // Build Dashboard URL
  const viewInDashboardUrl = useMemo(() => {
    if (!dashboardUrl) return undefined;
    const base = dashboardUrl.replace(/\/?overview\/?$/i, '').replace(/\/$/, '');
    
    if (type === 'pointer' && objectId) {
      return `${base}/browser/${targetClass}?filters=${encodeURIComponent(JSON.stringify([{ field: 'objectId', constraint: 'eq', value: objectId }]))}`;
    }
    if (type === 'relation' && sourceObjectId && relationField) {
      return `${base}/browser/${sourceClass}/${sourceObjectId}/${relationField}`;
    }
    return `${base}/browser/${targetClass}`;
  }, [dashboardUrl, type, targetClass, objectId, sourceClass, sourceObjectId, relationField]);

  // Get title
  const title = useMemo(() => {
    if (type === 'pointer') {
      return `${targetClass}: ${objectId?.slice(0, 10)}...`;
    }
    return `${relationField} → ${targetClass}`;
  }, [type, targetClass, objectId, relationField]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle pointer click within the modal
  const handleNestedPointerClick = (ptr: { className: string; objectId: string }) => {
    onPointerClick?.(ptr);
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-4xl max-h-[85vh] bg-[#1e1e1e] rounded-xl overflow-hidden shadow-2xl border border-white/10 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-[#252526] border-b border-[#3c3c3c]">
          <div className="flex items-center gap-3 min-w-0">
            {/* Type Icon */}
            {type === 'pointer' ? (
              <span className="text-[#208AEC]">→</span>
            ) : (
              <svg className="w-4 h-4 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            )}
            
            {/* Title */}
            <span className="text-sm font-medium text-white truncate">{title}</span>
            
            {/* Count badge for relations */}
            {type === 'relation' && !isLoading && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-500/20 text-violet-400">
                {count} {count === 1 ? 'object' : 'objects'}
              </span>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="flex items-center gap-3 text-white/50">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-48 text-red-400">
              <div className="text-center">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="mx-auto mb-2 text-red-400/50">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          ) : type === 'pointer' && data && !Array.isArray(data) ? (
            /* Pointer: Single object as key-value table */
            <div className="space-y-1">
              {sortFields(Object.keys(data as ParseRow)).map((key) => {
                const value = (data as ParseRow)[key];
                const isPtrValue = isPointer(value);
                
                return (
                  <div key={key} className="flex border-b border-white/5 py-2">
                    <div className="w-40 flex-shrink-0 text-xs text-white/50 font-mono">
                      {key}
                    </div>
                    <div className="flex-1 text-sm text-white/90 break-all">
                      {isPtrValue ? (
                        <button
                          onClick={() => handleNestedPointerClick({ 
                            className: value.className, 
                            objectId: value.objectId 
                          })}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full 
                                     bg-[#208AEC]/20 text-[#208AEC] text-xs
                                     hover:bg-[#208AEC]/30 transition-colors cursor-pointer"
                        >
                          <span className="text-[#208AEC]/60">→</span>
                          {value.className}:{value.objectId.slice(0, 8)}...
                        </button>
                      ) : (
                        <span className={value === null || value === undefined ? 'text-white/30 italic' : ''}>
                          {formatValue(value, key)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : type === 'relation' && Array.isArray(data) ? (
            /* Relation: Multiple objects as table */
            data.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-white/40">
                <div className="text-center">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="mx-auto mb-2 text-white/20">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M3 9h18M9 21V9" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  <p className="text-sm">No related objects</p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      {/* Get columns from first row */}
                      {sortFields(Object.keys(data[0] || {})).slice(0, 6).map((key) => (
                        <th key={key} className="px-3 py-2 text-left text-xs font-semibold text-white/50 uppercase">
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, idx) => (
                      <tr key={row.objectId || idx} className="border-b border-white/5 hover:bg-white/5">
                        {sortFields(Object.keys(data[0] || {})).slice(0, 6).map((key) => {
                          const value = row[key];
                          const isPtrValue = isPointer(value);
                          
                          return (
                            <td key={key} className="px-3 py-2 text-white/80">
                              {isPtrValue ? (
                                <button
                                  onClick={() => handleNestedPointerClick({ 
                                    className: value.className, 
                                    objectId: value.objectId 
                                  })}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full 
                                             bg-[#208AEC]/20 text-[#208AEC] text-xs
                                             hover:bg-[#208AEC]/30 transition-colors cursor-pointer"
                                >
                                  →{value.objectId.slice(0, 6)}
                                </button>
                              ) : (
                                <span className={value === null || value === undefined ? 'text-white/30 italic text-xs' : ''}>
                                  {formatValue(value, key)}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <div className="flex items-center justify-center h-48 text-white/40">
              No data available
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 bg-[#252526] border-t border-[#3c3c3c]">
          <div className="text-xs text-white/40">
            {type === 'pointer' ? 'Pointer' : 'Relation'} → {targetClass}
          </div>
          
          <div className="flex items-center gap-2">
            {viewInDashboardUrl && (
              <a
                href={viewInDashboardUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm
                           text-white/70 hover:text-white bg-white/5 hover:bg-white/10 
                           border border-white/10 transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11"/>
                  <path d="M15 3H21V9"/>
                  <path d="M10 14L21 3"/>
                </svg>
                View in Dashboard
              </a>
            )}
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-md text-sm font-medium
                         text-white bg-[#208AEC] hover:bg-[#208AEC]/80
                         transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

